import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { Row, RowHandle } from "./Row";
import styles from "./Layout.module.less";
import { BlobIdImageElementComposerViewModel } from "client/Api/Hub/common.composer";
import { AddRowButton } from "./AddRowButton";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	generateTrackingId,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { clearFocus } from "client/Misc/domUtils";
import { AlertManager, useAlerts, useResizeObserver } from "client/Hooks";
import { AddRowMenu, AddRowMenuHandle } from "./Menus/AddRowMenu";
import { useEmbedInputDialog } from "../../HubMediaSelect/EmbedInputDialog";
import { Guid } from "client_legacy/ndaw/guid";
import classNames from "classnames";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { duplicateElement } from "../Elements/Element";
import { useVideoInputDialog } from "../../HubMediaSelect/VideoInputDialog";
import {
	HubItemColumnFormModel,
	HubItemElementFormModel,
	HubItemLayoutFormModel,
	HubItemRowFormModel
} from "../../Management/common";
import { HubItemElementKind } from "client/Api/Hub/common";
import { BuilderContext } from "../BuilderContext";
import { BusyIndicator } from "client/Components";
import { useHubImageSelector } from "../../Management/useHubImageSelector";

export const ROW_BREAKPOINT_WIDTH = 796;
const ROW_CLICKABLE_PADDING = 16;

export type LayoutProps = {
	hubItemKind: HubItemKind;
	attachable: IValueInputAttachable<HubItemLayoutFormModel>;
	originalImages: Map<number, BlobIdImageElementComposerViewModel>;
};

export const Layout = (props: LayoutProps): ReactElement => {
	const {
		hubItemKind,
		attachable,
		originalImages
	} = props;

	const [isAddRowMenuVisible, setIsRowMenuVisible] = useState<boolean>(false);
	const [rowToScrollToId, setRowToScrollToId] = useState<Guid | undefined>(undefined);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const addRowButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const addRowMenuRef: RefObject<AddRowMenuHandle | null> = useRef<AddRowMenuHandle>(null);
	const alertManager: AlertManager = useAlerts();
	const rowRefs: ReadonlyRefObject<Map<Guid, RowHandle>> =
		useRef<Map<Guid, RowHandle>>(new Map());

	const setRowRef = useCallback(
		(row: RowHandle | null, id: Guid): void => {
			if (isNil(row)) {
				return;
			}

			rowRefs.current
				.set(id, row);
		},
		[]
	);

	const [embedInputDialog, openEmbedInputDialog] = useEmbedInputDialog();
	const [videoInputDialog, openVideoInputDialog] = useVideoInputDialog();

	const {
		imageRestrictions,
		defaultImageLocation
	} = useContext(BuilderContext);

	const {
		dialogElement: selectImageDialog,
		onSelect: onSelectImage
	} = useHubImageSelector({
		onIsBusyChange: setIsBusy,
		supportedImageExtensions: imageRestrictions.supportedFileExtensions,
		defaultImageLocation,
		alertManager,
		supportedMimeTypes: imageRestrictions.supportedMimeTypes,
		maxImageSize: imageRestrictions.maxImageSize
	});

	const layoutRootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const { width } = useResizeObserver(layoutRootRef);

	const hasStackedColumns: boolean = useMemo(
		(): boolean => width < ROW_BREAKPOINT_WIDTH + ROW_CLICKABLE_PADDING * 2,
		[width]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			const rowsCopy: ObjectWithClientId<HubItemRowFormModel>[] = [
				...attachable.for("rows")
					.value
			];
			const itemToMove: ObjectWithClientId<HubItemRowFormModel> = rowsCopy.splice(fromIndex, 1)[0];

			rowsCopy.splice(
				toIndex,
				0,
				itemToMove
			);

			attachable.for("rows")
				.reducer
				.setValue(
					rowsCopy
				);

			setRowToScrollToId(getClientId(itemToMove));

			attachable.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	const addRowHandler = useCallback(
		(row: ObjectWithClientId<HubItemRowFormModel>): void => {
			const rowsAttachable: IValueInputAttachable<readonly ObjectWithClientId<HubItemRowFormModel>[]> = attachable.for("rows");

			rowsAttachable
				.reducer
				.push(row);

			rowsAttachable.validation
				.triggerInputFinished();

			setTimeout(
				(): void => {
					const lastRowIndex: number = rowsAttachable.reducer
						.value
						.length - 1;

					if (lastRowIndex >= 0) {
						rowsAttachable.forIndex(lastRowIndex)
							.for("columns")
							.forEach(column => {
								column.for("elements")
									.forEach(element => {
										element.whenType(
											x => x.kind === HubItemElementKind.Image,
											image => image.for("image")
												.validation
												.triggerInputFinished()
										);
									});
							});
					}
				},
				1
			);
		},
		[attachable]
	);

	const deleteRowHandler = useCallback(
		(index: number): void => {
			attachable.for("rows")
				.reducer.removeAt(index);

			attachable.validation
				.triggerInputFinished();

			clearFocus();
		},
		[attachable]
	);

	const duplicateHandler = useCallback(
		async (index: number): Promise<void> => {
			const rowsCopy: ObjectWithClientId<HubItemRowFormModel>[] = [
				...attachable.for("rows")
					.value
			];

			const itemToDuplicate: ObjectWithClientId<HubItemRowFormModel> = attachable.for("rows")
				.reducer
				.forIndex(index)
				.value;

			const duplicatedRow: ObjectWithClientId<HubItemRowFormModel> = await duplicateRow(
				itemToDuplicate,
				originalImages,
				hubItemKind
			);

			rowsCopy.splice(
				index,
				0,
				duplicatedRow
			);

			attachable.for("rows")
				.reducer
				.setValue(rowsCopy);

			attachable.validation
				.triggerInputFinished();

			clearFocus();
		},
		[
			attachable,
			hubItemKind,
			originalImages
		]
	);

	const findAndFocusHandler = useCallback(
		(clientId: Guid): void => {
			rowRefs.current
				.forEach((x) => x?.findAndFocus(clientId));
		},
		[]
	);

	useLayoutEffect(
		(): void => {
			if (isNotNil(rowToScrollToId)) {
				rowRefs.current
					.get(rowToScrollToId)
					?.scrollIntoView();

				setRowToScrollToId(undefined);
			}
		},
		[rowToScrollToId]
	);

	const onAddRowButtonClick = useCallback(
		(): void => {
			setIsRowMenuVisible(true);

			// We wait for the menu to be visible before focusing the first element
			setTimeout(
				(): void => {
					addRowMenuRef.current
						?.focusFirstElement();
				},
				0
			);
		},
		[]
	);

	const focusAddButtonHandler = useCallback(
		(): void => {
			addRowButtonRef.current
				?.focus();
		},
		[]
	);

	return (
		<div
			{...setTrackingId(attachable.reducer.path)}
			className={styles.root}
			ref={layoutRootRef}
		>
			{isBusy && <BusyIndicator />}
			{
				attachable.for("rows")
					.map((rowAttachable, index) => {
						const clientId: Guid = getClientId(rowAttachable.value);

						return (
							<Row
								ref={(r) => setRowRef(r, clientId)}
								key={clientId}
								hubItemKind={hubItemKind}
								layoutLocation={{ row: index }}
								attachable={rowAttachable}
								onDelete={() => deleteRowHandler(index)}
								onMoveDown={index < attachable.for("rows").value.length - 1
									? () => itemMoveHandler(index, index + 1)
									: undefined
								}
								onMoveUp={index > 0
									? () => itemMoveHandler(index, index - 1)
									: undefined
								}
								onDuplicate={() => duplicateHandler(index)}
								originalImages={originalImages}
								hasStackedColumns={hasStackedColumns}
								onOpenEmbedInputDialog={openEmbedInputDialog}
								onOpenVideoInputDialog={openVideoInputDialog}
								onSelectImage={onSelectImage}
							/>
					);
				})
			}
			<div className={classNames(
				styles.addNewRowSection,
				{[styles.addNewRowSection_active]: isAddRowMenuVisible}
			)}>
				<AddRowButton
					onClick={onAddRowButtonClick}
					ref={addRowButtonRef}
					trackingId="add-row-button"
				/>
				{isAddRowMenuVisible && addRowButtonRef.current && (
					<AddRowMenu
						ref={addRowMenuRef}
						onFindAndFocus={findAndFocusHandler}
						onNewRow={addRowHandler}
						target={addRowButtonRef.current}
						onClose={() => setIsRowMenuVisible(false)}
						onFocusAddButton={focusAddButtonHandler}
						onOpenEmbedInputDialog={openEmbedInputDialog}
						onOpenVideoInputDialog={openVideoInputDialog}
						trackingId={generateTrackingId(attachable.reducer.path, "__add-row-menu")}
						onSelectImage={onSelectImage}
					/>
				)}
			</div>
			{embedInputDialog}
			{videoInputDialog}
			{selectImageDialog}
		</div>
	);
};
Layout.displayName="Builder.Layout";

async function duplicateRow(
	row: ObjectWithClientId<HubItemRowFormModel>,
	originalImages: Map<number, BlobIdImageElementComposerViewModel>,
	hubItemKind: HubItemKind
): Promise<ObjectWithClientId<HubItemRowFormModel>> {
	const columns: ObjectWithClientId<HubItemColumnFormModel>[] = [];

	for (const column of row.columns) {
		const elements: ObjectWithClientId<HubItemElementFormModel>[] = [];

		for (const element of column.elements) {
			const duplicatedElement: ObjectWithClientId<HubItemElementFormModel> = await duplicateElement(
				element,
				originalImages,
				hubItemKind
			);

			elements.push(duplicatedElement);
		}

		columns.push(setClientId({ elements }));
	}

	return setClientId({
		...row,
		columns
	});
}
